import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, Building, Star, LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StripeService } from "@/services/stripe-service";
import { ContactService } from "@/services/contact-service";
import { HexIcon } from "@/components/ui/hex-icon";

interface PricingPlan {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
  icon: LucideIcon;
  ctaText: string;
  ctaVariant: "default" | "outline";
  planType?: "free" | "basic" | "premium" | "enterprise";
}

const plans: PricingPlan[] = [
  {
    name: "Basic",
    price: "Contact Us",
    description: "Great for small teams",
    icon: Star,
    features: [
      "3 subdomains",
      "10 users",
      "5GB storage",
      "10 data configurations",
      "AI credits included",
      "Priority support"
    ],
    ctaText: "Enquire",
    ctaVariant: "outline",
    planType: "basic"
  },
  {
    name: "Premium",
    price: "Contact Us",
    description: "For growing organizations",
    icon: Crown,
    popular: true,
    features: [
      "Custom DNS",
      "SSO integration",
      "50 users",
      "10GB storage",
      "Unlimited data configurations",
      "Advanced AI credits",
      "Priority support"
    ],
    ctaText: "Enquire",
    ctaVariant: "outline",
    planType: "premium"
  },
  {
    name: "Enterprise",
    price: "Contact Us",
    description: "For large enterprises",
    icon: Building,
    features: [
      "Custom subdomains",
      "Unlimited users",
      "Unlimited storage",
      "Custom integrations",
      "Dedicated support",
      "SLA guarantees",
      "Custom AI training"
    ],
    ctaText: "Enquire",
    ctaVariant: "outline",
  }
];

export const PricingPlans = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePlanAction = async (plan: PricingPlan) => {
    // All plans now use the contact modal
    setIsContactModalOpen(true);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await ContactService.sendEnterpriseInquiry({
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        message: contactForm.message
      });

      if (result.success) {
        alert("Thank you for your inquiry! We'll get back to you soon.");
        setIsContactModalOpen(false);
        setContactForm({ name: "", email: "", company: "", message: "" });
      } else {
        throw new Error(result.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      alert("There was an error sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="hex-section hex-gradient-bg">
        <div className="hex-container">
          <div className="text-center mb-12">
            <h2 className="hex-heading">Choose Your Plan</h2>
            <div className="hex-divider"></div>
            <p className="hex-subtitle">
              Scale your manufacturing intelligence with flexible pricing plans designed for teams of all sizes.
            </p>
          </div>

          <div className="hex-grid hex-grid-3 max-w-7xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`hex-card hex-hover-lift relative ${
                  plan.popular
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="hex-badge">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <HexIcon icon={plan.icon} className="mx-auto mb-4" />
                  <h3 className="hex-subheading">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                    {plan.price !== "Contact Us" && (
                      <span className="text-muted-foreground">/month</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.ctaVariant}
                  className="w-full"
                  onClick={() => handlePlanAction(plan)}
                >
                  {plan.ctaText}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Modal for All Plans */}
      <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enquire</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={contactForm.company}
                onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder="Tell us about your requirements..."
                required
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsContactModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}; 